import { Team } from '@litlingo/client';
import { setURLParams } from 'actions';
import LoadingIndicator from 'components/LoadingIndicator';
import useClickOutside from 'components/utils/useClickOutside';
import { SEARCH_ICON } from 'constants/filterIcons';
import { TEAM_FILTER_CONFIG, TeamKind } from 'utils/teams';
import useTeams from 'hooks/teams/useTeams';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { getNavParamsByResource } from 'selectors/nav';
import { getTeamUsersCount } from 'selectors/users';
import { useSelector } from 'store';
import logEvent from 'utils/analytics';

type Props = {
  className?: string;
  resource: string;
  kind: TeamKind;
};

const UserTeamsFilter: React.FC<Props> = ({ className, resource, kind }) => {
  const dispatch = useDispatch();
  const config = TEAM_FILTER_CONFIG[kind];

  const params = useSelector(getNavParamsByResource(resource));

  const normalizedKind = kind.replace('-', '_');

  const filters = (params[`${normalizedKind}_uuids`] || []) as string[];
  const excludedFilters = (params[`not_${normalizedKind}_uuids`] || []) as string[];
  const noTeam = (params.no_team || '') as string;

  const teamParamKey = `${resource}__${normalizedKind}_uuids`;
  const notTeamParamKey = `${resource}__not_${normalizedKind}_uuids`;

  const teamsUserCount = useSelector(getTeamUsersCount);

  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const { data, isLoading } = useTeams({
    redirectOnError: false,
    broad_search: debouncedSearch,
    kind,
  });

  const options = data?.records || [];
  const sortedOptions = [...options].sort((a, b) => a.name.localeCompare(b.name));

  const wrapperRef = useRef(null);
  useClickOutside(wrapperRef, () => {
    setSearch('');
    setIsOpen(false);
  });

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 500);
    return () => clearTimeout(t);
  }, [search]);

  const handleInputChange = (
    e: React.MouseEvent<HTMLInputElement, MouseEvent>,
    team: Team
  ): void => {
    logEvent(`use-cases-enabled-filter-${team.uuid}`);
    e.stopPropagation();

    const exclude = e.nativeEvent.shiftKey;

    const isIncluded = filters.includes(team.uuid);
    const isExcluded = excludedFilters.includes(team.uuid);

    let nextIncluded = [...filters];
    let nextExcluded = [...excludedFilters];

    if (exclude) {
      if (isExcluded) {
        nextExcluded = nextExcluded.filter((id) => id !== team.uuid);
      } else {
        nextIncluded = nextIncluded.filter((id) => id !== team.uuid);
        nextExcluded.push(team.uuid);
      }
    } else if (isIncluded) {
      nextIncluded = nextIncluded.filter((id) => id !== team.uuid);
    } else {
      nextExcluded = nextExcluded.filter((id) => id !== team.uuid);
      nextIncluded.push(team.uuid);
    }

    dispatch(
      setURLParams({
        [`${resource}__no_team`]: '',
        [teamParamKey]: nextIncluded,
        [notTeamParamKey]: nextExcluded,
        [`${resource}__offset`]: '0',
      })
    );
  };

  const handleNoTeam = (e: React.ChangeEvent<HTMLInputElement>): void => {
    e.stopPropagation();

    dispatch(
      setURLParams(
        noTeam !== 'true'
          ? {
              [`${resource}__no_team`]: 'true',
              [teamParamKey]: [],
              [`${resource}__offset`]: '0',
            }
          : {
              [`${resource}__no_team`]: '',
              [`${resource}__offset`]: '0',
            }
      )
    );
  };

  const renderOptions = (): JSX.Element => {
    if (isLoading) {
      return (
        <div className="flex h-20 items-center justify-center">
          <LoadingIndicator size="8" />
        </div>
      );
    }

    if (!sortedOptions.length) {
      return <div className="text-small m-2">{config.emptyMessage}</div>;
    }

    return (
      <fieldset className="m-2 flex flex-col gap-2">
        {sortedOptions.map((team) => (
          <div key={team.uuid} className="flex justify-between cursor-pointer">
            <label className="flex items-center gap-1" htmlFor={`team-${team.uuid}`}>
              <input
                id={`team-${team.uuid}`}
                type="checkbox"
                checked={filters.includes(team.uuid) || excludedFilters.includes(team.uuid)}
                onClick={(e): void => handleInputChange(e, team)}
                className={`form-checkbox litlingo-checkbox h-4 w-4 transition duration-150 ease-in-out cursor-pointer  ${
                  excludedFilters.includes(team.uuid) ? 'litlingo-checkbox-negated' : ''
                }`}
              />
              <div className="text-small break-all">{team.name}</div>
            </label>
            <div className="text-small">
              {teamsUserCount[team.uuid] > 0 ? teamsUserCount[team.uuid] : '-'}
            </div>
          </div>
        ))}

        <label className="flex items-center gap-1 cursor-pointer" htmlFor="no-team">
          <input
            id="no-team"
            type="checkbox"
            checked={noTeam === 'true'}
            onChange={handleNoTeam}
            className="form-checkbox litlingo-checkbox"
          />
          <div className="text-small">{config.emptyLabel}</div>
        </label>
      </fieldset>
    );
  };

  return (
    <div ref={wrapperRef} className={`relative w-full ${className || ''}`}>
      <div className="relative flex items-center h-8 bg-white">
        <div className="absolute left-0 ml-1.5 w-5">{SEARCH_ICON()}</div>
        <input
          className={`rounded border border-litlingo-gray-2 h-full w-full text-litlingo-gray-6 pl-8 text-body placeholder-italic  ${
            filters.length ? 'placeholder-litlingo-gray-6' : 'placeholder-litlingo-gray-4'
          }`}
          value={search}
          placeholder={config.placeholder}
          onChange={(e): void => setSearch(e.target.value.toLowerCase())}
          onFocus={(): void => {
            setIsOpen(true);
          }}
          autoComplete="off"
        />
        {filters.length > 0 && !isOpen && (
          <div className="flex absolute right-0 mr-1.5 items-center justify-center w-4.5 h-4.5 rounded-full bg-litlingo-primary-60">
            <span>{filters.length}</span>
          </div>
        )}
      </div>

      {isOpen && (
        <div className="absolute z-40 mt-1 w-52 rounded-md bg-white shadow-lg">
          <div className="max-h-select-dropdown overflow-auto custom-scrollbar">
            {renderOptions()}
          </div>
        </div>
      )}
    </div>
  );
};

export default UserTeamsFilter;
